import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Plus, Pill, Clock, Sun, Sunset, Moon,
  AlertTriangle, ExternalLink, Trash2, RotateCcw,
  ShoppingCart, Package, ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMING_CONFIG = {
  morning: { label: 'Morning', icon: Sun, color: 'bg-amber-50 border-amber-200 text-amber-800' },
  afternoon: { label: 'Afternoon', icon: Clock, color: 'bg-sky-50 border-sky-200 text-sky-800' },
  evening: { label: 'Evening', icon: Sunset, color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
};

export default function MyStackPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stack, setStack] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStack = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stack`, { headers: getAuthHeader() });
      setStack(res.data);
    } catch (err) {
      console.error('Stack fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user) { navigate('/auth?redirect=/my-stack'); return; }
    fetchStack();
  }, [user, navigate, fetchStack]);

  const handleRemove = async (itemId, name) => {
    try {
      await axios.delete(`${API}/stack/${itemId}`, { headers: getAuthHeader() });
      toast.success(`${name} removed from stack`);
      fetchStack();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleRestock = async (itemId, name) => {
    try {
      await axios.post(`${API}/stack/${itemId}/restock`, {}, { headers: getAuthHeader() });
      toast.success(`${name} restocked! Timer reset.`);
      fetchStack();
    } catch (err) {
      toast.error('Failed to restock');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { items = [], schedule = {}, restock_alerts = [] } = stack || {};

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="mystack-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="mystack-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">My Stack</h1>
          <p className="text-sm text-muted-foreground">{items.length} active supplement{items.length !== 1 ? 's' : ''}</p>
        </motion.div>

        {/* Restock Alerts */}
        {restock_alerts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            {restock_alerts.map((alert, i) => (
              <div key={i} className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-2 flex items-center justify-between" data-testid="restock-alert">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">{alert.name} running low</p>
                    <p className="text-xs text-amber-700">
                      {alert.days_until_restock <= 0 ? 'Time to restock!' : `${alert.days_until_restock} days until restock`}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleRestock(alert.id, alert.name)} className="border-amber-300 text-amber-800 hover:bg-amber-100">
                  <RotateCcw className="w-3 h-3 mr-1" /> Restocked
                </Button>
              </div>
            ))}
          </motion.div>
        )}

        {/* Daily Schedule View */}
        {items.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-8">
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Daily Schedule
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="daily-schedule">
              {Object.entries(TIMING_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const timingItems = schedule[key] || [];
                return (
                  <div key={key} className={`rounded-xl border p-4 ${config.color}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4" />
                      <h3 className="text-sm font-semibold">{config.label}</h3>
                      <span className="ml-auto text-xs opacity-70">{timingItems.length}</span>
                    </div>
                    {timingItems.length > 0 ? (
                      <div className="space-y-2">
                        {timingItems.map((item) => (
                          <div key={item.id} className="bg-white/60 rounded-lg p-2.5 text-xs">
                            <p className="font-medium">{item.name}</p>
                            {item.dosage && <p className="opacity-70">{item.dosage}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs opacity-50">No supplements</p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* All Supplements */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            All Supplements
          </h2>

          {items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-border p-8 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-[#1A1A1A] mb-1">Your stack is empty</h3>
              <p className="text-sm text-muted-foreground mb-4">Add supplements from your assessment results</p>
              <Button asChild className="btn-primary">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="stack-items">
              {items.map((item, i) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }} className="bg-white rounded-xl border border-border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-[#1A1A1A]">{item.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {item.dosage && <span className="text-xs text-muted-foreground">{item.dosage}</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${TIMING_CONFIG[item.timing]?.color || 'bg-gray-100'}`}>
                          {TIMING_CONFIG[item.timing]?.label || item.timing}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>Started {item.date_started}</span>
                        <span>{item.supply_days}-day supply</span>
                      </div>
                    </div>
                    <button onClick={() => handleRemove(item.id, item.name)} className="text-muted-foreground hover:text-red-500 transition-colors p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Purchase links */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                    {item.purchase_url_amazon && (
                      <a href={item.purchase_url_amazon} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-orange-700 bg-orange-50 px-3 py-1.5 rounded-lg hover:bg-orange-100 transition-colors">
                        <ShoppingCart className="w-3 h-3" /> Amazon
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                    {item.purchase_url_iherb && (
                      <a href={item.purchase_url_iherb} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100 transition-colors">
                        <ShoppingCart className="w-3 h-3" /> iHerb
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
